import React, { ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { Alert, Button, Space } from 'antd';
import { ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { trackException } from '../../utils/monitoring';
import { isNetworkError } from '../../utils/errorHandling';

interface ComponentErrorBoundaryProps {
  children: ReactNode;
  componentName?: string;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
  componentName?: string;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  componentName = 'Component',
}) => {
  const isNetwork = isNetworkError(error);

  const errorMessage = isNetwork
    ? 'Unable to load data. Please check your connection.'
    : `${componentName} encountered an error and needs to be reloaded.`;

  const errorType = isNetwork ? 'warning' : 'error';

  return (
    <div style={{ padding: '16px' }}>
      <Alert
        type={errorType}
        showIcon
        icon={<ExclamationCircleOutlined />}
        message={`${componentName} Error`}
        description={
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>{errorMessage}</div>
            <div style={{ marginTop: '8px' }}>
              <Button
                type="primary"
                size="small"
                icon={<ReloadOutlined />}
                onClick={resetErrorBoundary}
              >
                Retry
              </Button>
            </div>
          </Space>
        }
      />
    </div>
  );
};

/**
 * Component-level error boundary that provides graceful error handling
 * for individual components without crashing the entire page.
 */
const ComponentErrorBoundary: React.FC<ComponentErrorBoundaryProps> = ({
  children,
  componentName = 'Component',
  fallback,
  onError,
}) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log error to monitoring
    trackException(error, {
      componentName,
      componentStack: errorInfo.componentStack,
    });

    // Call custom error handler if provided
    onError?.(error, errorInfo);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`Error in ${componentName}:`, error);
      console.error('Component stack:', errorInfo.componentStack);
    }
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={
        fallback
          ? () => fallback as React.ReactElement
          : (props) => (
              <ErrorFallback {...props} componentName={componentName} />
            )
      }
      onError={handleError}
      onReset={() => {
        // Force component remount by triggering a hash change
        const currentHash = window.location.hash;
        window.location.hash = '';
        setTimeout(() => {
          window.location.hash = currentHash;
        }, 0);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ComponentErrorBoundary;
